import { Text } from '@/components/text';
import { useLanguageStore } from '@/stores/language-store';
import { formatNumber, parseNumber } from '@/utils/formatters';
import { FieldLabel } from './field-label';

import { Colors } from '@/constants/theme';
import React from 'react';
import { StyleSheet, TextInput, View } from 'react-native';

function clamp(n: number, min?: number, max?: number) {
  let v = n;
  if (typeof min === 'number') v = Math.max(min, v);
  if (typeof max === 'number') v = Math.min(max, v);
  return v;
}

export function NumberInput({
  label,
  description,
  value,
  placeholder,
  onChange,
  suffix,
  prefix,
  min,
  max,
  disabled,
  style,
  id,
  name,
  size = 'default',
  onFocus,
  onBlur,
}: {
  label?: string;
  description?: string;
  value: number | null;
  placeholder?: string;
  onChange: (v: number | null) => void;
  suffix?: string;
  prefix?: string;
  min?: number;
  max?: number;
  disabled?: boolean;
  style?: any;
  id?: string;
  name?: string;
  size?: 'default' | 'sm';
  onFocus?: () => void;
  onBlur?: () => void;
}) {
  const [text, setText] = React.useState('');
  const languageCode = useLanguageStore(s => s.locale);

  // sync externo → texto formateado
  React.useEffect(() => {
    if (value == null) {
      setText('');
      return;
    }

    setText(formatNumber(value, languageCode));
  }, [value, languageCode]);

  const isSmall = size === 'sm';

  return (
    <View style={[styles.container, style]}>
      {label ? <FieldLabel label={label} description={description} /> : null}

      <View
        style={[
          styles.wrap,
          isSmall && styles.wrapSmall,
          disabled && styles.disabledWrap,
        ]}>
        {prefix ? (
          <Text style={[styles.prefix, isSmall && styles.textSmall]}>
            {prefix}
          </Text>
        ) : null}
        <TextInput
          {...({ name } as any)}
          nativeID={id}
          value={text}
          onChangeText={txt => {
            // Filter non-numeric characters (allow digits and local decimal separator)
            const cleaned = txt.replace(/[^0-9,.]/g, '');
            setText(cleaned);

            const n = parseNumber(cleaned, languageCode);
            if (n == null) return onChange(null);

            const clamped = clamp(n, min, max);
            if (clamped !== n) {
              setText(formatNumber(clamped, languageCode));
            }
            onChange(clamped);
          }}
          onFocus={onFocus}
          onBlur={() => {
            const n = parseNumber(text, languageCode);
            const defaultValue = min ?? 0;
            
            if (n == null) {
              setText(formatNumber(defaultValue, languageCode));
              onChange(defaultValue);
            } else {
              const clamped = clamp(n, min, max);
              setText(formatNumber(clamped, languageCode));
              onChange(clamped);
            }
            if (onBlur) onBlur();
          }}
          placeholder={placeholder}
          placeholderTextColor={Colors.light.placeholderText}
          keyboardType='numeric'
          editable={!disabled}
          style={[styles.input, isSmall && styles.textSmall]}
        />

        {suffix ? (
          <Text style={[isSmall && styles.textSmall, { flexShrink: 0 }]}>
            {suffix}
          </Text>
        ) : null}
      </View>
    </View>
  );
}
const styles = StyleSheet.create({
  container: {
    gap: 6,
  },
  wrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    minHeight: 48,
    borderWidth: 1,
    borderColor: '#898b8c',
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: '#fff',
  },
  wrapSmall: {
    minHeight: 30,
    paddingHorizontal: 6,
    paddingVertical: 2,
    gap: 2,
    borderRadius: 6,
  },
  input: {
    flex: 1,
    outlineStyle: 'none' as any, // web
    fontSize: 14,
    fontFamily: 'lexend-regular',
    paddingHorizontal: 0,
    margin: 0,
    minWidth: 0,
    flexShrink: 1,
  },
  textSmall: {
    fontSize: 12,
    textAlign: 'right',
  },
  disabledWrap: { opacity: 0.6 },
  prefix: {
    fontSize: 14,
  },
});
